import React, {
  KeyboardEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Dialog } from '@capacitor/dialog';

import { useDentist } from '@/contexts/DentistContext';
import { useRequests } from '@/contexts/RequestsContext';
import { useTracker } from '@/contexts/TrackerContext';
import {
  Patient,
  createPatient,
  getPatientsByDentistId
} from '@/data/Patients';
import { Request, createRequest } from '@/data/Requests';
import {
  Service,
  getDefaultServices,
  getServicesByDentistId
} from '@/data/Services';
import { useInputFocus, useModal, useToast } from '@/hooks';
import {
  OFF_DUTY_PATIENT_ID,
  PURPOSE_JOIN_SEPARATOR,
  REQUEST_STATUS_OPTIONS,
  SELECT_MODAL_OTHERS
} from '@/utils/constants';
import { formatName } from '@/utils/helpers/formatName';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { EVENTS } from '@/utils/tracker';
import { Gender } from '@/utils/types';
import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  addMinutes,
  format,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isValid,
  startOfDay
} from 'date-fns';

interface ManualBookingContextProps {
  // Date and Time
  date: string | null;
  setDate: React.Dispatch<React.SetStateAction<string | null>>;
  startTime: string | null;
  setStartTime: React.Dispatch<React.SetStateAction<string | null>>;
  endTime: string | null;
  setEndTime: React.Dispatch<React.SetStateAction<string | null>>;
  timeOptions: string[];

  // Purpose
  purpose: string | null;
  setPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  othersPurpose: string | null;
  setOthersPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  purposeOptions: string[];
  isPurposeSelected: (value: string) => boolean;
  isPurposePreSelected: (value: string) => boolean;
  onSelectPurpose: (value: string, isSelected: boolean) => void;
  isPurposeSelectModalOpen: boolean;
  onOpenPurposeSelectModal: () => void;
  onClosePurposeSelectModal: () => void;

  // Patient Search
  patients: Patient[];
  isPatientSearchModalOpen: boolean;
  onOpenPatientSearchModal: () => void;
  onPressPatient: (patient: Patient) => void;
  onClosePatientSearchModal: () => void;

  // Patient Information
  firstName: string | null;
  setFirstName: React.Dispatch<React.SetStateAction<string | null>>;
  lastName: string | null;
  setLastName: React.Dispatch<React.SetStateAction<string | null>>;
  mobileNumber: string | null;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  gender: Gender | null;
  setGender: React.Dispatch<React.SetStateAction<Gender | null>>;
  birthday: string | null;
  setBirthday: React.Dispatch<React.SetStateAction<string | null>>;

  // Miscellaneous
  errors: any;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  onInput: (field: string, value: any) => void;
  submit: () => Promise<void>;
  reset: () => void;
  onKeyDown: KeyboardEventHandler<HTMLIonInputElement>;
  conflictingRequest: Request | null;
  inputRef: React.RefObject<HTMLIonInputElement>;
}

const ManualBookingContext = createContext<
  ManualBookingContextProps | undefined
>(undefined);

interface ManualBookingProviderProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  selectedDate?: Date | string | null | undefined;
  setSelectedDate?: (date: Date) => void;
}

export const ManualBookingProvider: React.FC<ManualBookingProviderProps> = ({
  isOpen,
  onClose,
  selectedDate,
  setSelectedDate,
  children
}) => {
  const { selectedDentist: dentist, dentistHours } = useDentist();
  const { requests, setRequests, setFocusedRequestId } = useRequests();
  const { captureEvent } = useTracker();
  const displayToast = useToast();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLIonInputElement>(null);

  const [date, setDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [othersPurpose, setOthersPurpose] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [lastSavedPurpose, setLastSavedPurpose] = useState<string | null>(null);
  const {
    isOpen: isPurposeSelectModalOpen,
    onOpen: onOpenPurposeSelectModal,
    onClose: onClosePurposeSelectModal
  } = useModal();
  const {
    isOpen: isPatientSearchModalOpen,
    onOpen: onOpenPatientSearchModal,
    onClose: onClosePatientSearchModal
  } = useModal();
  const [errors, setErrors] = useState<Record<string, boolean>>({
    date: false,
    startTime: false,
    endTime: false,
    purpose: false,
    othersPurpose: false,
    firstName: false,
    lastName: false,
    mobileNumber: false
  });

  const { data: dentistServices = [] }: UseQueryResult<Service[], Error> =
    useQuery({
      queryKey: ['services', dentist?.id],
      queryFn: () => getServicesByDentistId(dentist?.id!),
      enabled: isOpen && !!dentist?.id
    });

  const { data: defaultServices = [] }: UseQueryResult<Service[], Error> =
    useQuery({
      queryKey: ['defaultServices'],
      queryFn: getDefaultServices
    });

  const { data: patientsByDentist = [] }: UseQueryResult<Patient[], Error> =
    useQuery({
      queryKey: ['patientsByDentist', dentist?.id],
      queryFn: () => getPatientsByDentistId(dentist?.id!),
      enabled: isOpen && !!dentist?.id
    });

  const services: string[] = useMemo(() => {
    if (dentistServices.length > 0) {
      return dentistServices.map((service) => service.name);
    }
    return defaultServices.map((service) => service.name);
  }, [dentistServices, defaultServices]);

  const createPatientMutation = useMutation({
    mutationFn: async (newPatient: Patient) => {
      const createdPatient = await queryClient.fetchQuery({
        queryKey: [
          'createPatient',
          newPatient.first_name,
          newPatient.last_name
        ],
        queryFn: () => createPatient(newPatient)
      });
      if (!createdPatient?.id) {
        throw new Error('Failed to create patient');
      }
      return createdPatient;
    },
    onError: () => {
      displayToast({
        message:
          'Failed to create patient. Please check your internet connection and try again.',
        duration: 3000,
        position: 'bottom'
      });
    }
  });

  const createRequestMutation = useMutation({
    mutationFn: async ({
      request,
      patient
    }: {
      request: Request;
      patient: Patient;
    }) => {
      const createdRequest = await queryClient.fetchQuery({
        queryKey: ['createRequest', request.dentist_id, request.date],
        queryFn: () => createRequest(request)
      });
      if (!createdRequest) {
        throw new Error('Failed to create request');
      }
      return { createdRequest, patient };
    },
    onSuccess: ({ createdRequest, patient }) => {
      setRequests([...requests, { ...createdRequest, patient }]);
      captureEvent({
        event: EVENTS.MANUAL_BOOKING_CREATED,
        properties: { request_id: createdRequest.id! }
      });
      reset();
      if (onClose) onClose();
      if (setSelectedDate && date) {
        setSelectedDate(startOfDay(parseDate(date)));
      }
      if (createdRequest.id) {
        setFocusedRequestId(createdRequest.id);
      }
    },
    onError: () => {
      displayToast({
        message:
          'Failed to create appointment. Please check your internet connection and try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'create-request-button'
      });
    }
  });

  useInputFocus(inputRef);

  const getTimeOptions = (start: string, end: string) => {
    const startTime = parseTime(start);
    const endTime = parseTime(end);
    const calculatedTimeOptions = [];

    let currentTime = startTime;
    while (isBefore(currentTime, endTime)) {
      calculatedTimeOptions.push(format(currentTime, 'HH:mm'));
      currentTime = addMinutes(currentTime, 15);
    }

    return calculatedTimeOptions;
  };

  const timeOptions = useMemo(() => {
    if (dentistHours.length > 0) {
      const dayOfWeek = getDay(parseDate(date));
      const hoursForDay = dentistHours.find((hours) => hours.day === dayOfWeek);
      if (hoursForDay && hoursForDay.start_time && hoursForDay.end_time) {
        return getTimeOptions(hoursForDay.start_time, hoursForDay.end_time);
      }
    }
    return Array.from({ length: 96 }, (_, i) => {
      const totalMinutes = 8 * 60 + i * 15;
      const hours = String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0');
      const minutes = String(totalMinutes % 60).padStart(2, '0');
      return `${hours}:${minutes}`;
    });
  }, [dentistHours, date]);

  const onInput = (field: string, value: any) => {
    switch (field) {
      case 'date':
        setDate(value);
        break;
      case 'startTime':
        setStartTime(value);
        break;
      case 'endTime':
        setEndTime(value);
        break;
      case 'purpose':
        setPurpose(value);
        break;
      case 'othersPurpose':
        setOthersPurpose(value);
        break;
      case 'firstName':
        setFirstName(value);
        break;
      case 'lastName':
        setLastName(value);
        break;
      case 'mobileNumber': {
        if (value === '' || value?.startsWith('9')) {
          setMobileNumber(value);
        } else {
          setMobileNumber('');
          displayToast({
            message:
              'Please enter your mobile number starting with 9 (e.g., 9xxxxxxx, not 09xxxxxxx).',
            duration: 5000,
            position: 'top'
          });
        }
        break;
      }
      case 'gender':
        setGender(value);
        break;
      case 'birthday':
        setBirthday(value);
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
  };

  const reset = () => {
    setDate(
      selectedDate && isValid(selectedDate)
        ? format(startOfDay(selectedDate), 'yyyy-MM-dd')
        : null
    );
    setStartTime(null);
    setEndTime(null);
    setPurpose(null);
    setOthersPurpose(null);
    setFirstName(null);
    setLastName(null);
    setMobileNumber(null);
    setGender(null);
    setBirthday(null);
    setErrors({
      date: false,
      startTime: false,
      endTime: false,
      purpose: false,
      othersPurpose: false,
      firstName: false,
      lastName: false,
      mobileNumber: false
    });
  };

  const submit = useCallback(async () => {
    const newErrors = {
      date: !date,
      startTime: !startTime,
      endTime: !endTime,
      purpose: !purpose,
      othersPurpose: purpose === SELECT_MODAL_OTHERS && !othersPurpose,
      firstName: !formatName(firstName),
      lastName: !formatName(lastName),
      mobileNumber: !mobileNumber || mobileNumber.length !== 10
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      displayToast({
        message: 'Please fill in all required fields.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'create-request-button'
      });
      return;
    }

    if (!dentist?.id) {
      setErrors((prevErrors) => ({ ...prevErrors, dentist: true }));
      displayToast({
        message: 'Please select a dentist.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'create-request-button'
      });
      return;
    }

    const newPatient: Patient = {
      first_name: formatName(firstName),
      last_name: formatName(lastName),
      mobile_number: mobileNumber,
      gender: gender satisfies Gender | null,
      birthday: birthday,
      dentist_id: dentist.id
    };

    const createdPatient = await createPatientMutation.mutateAsync(newPatient);

    const newRequest: Request = {
      date: date || null,
      start_time: startTime || null,
      end_time: endTime || null,
      purpose:
        purpose === SELECT_MODAL_OTHERS
          ? `${purpose}: ${othersPurpose}`
          : purpose,
      status: REQUEST_STATUS_OPTIONS.APPROVED,
      dentist_id: dentist.id,
      patient_id: createdPatient.id || null
    };

    await createRequestMutation.mutateAsync({
      request: newRequest,
      patient: createdPatient
    });
  }, [
    date,
    startTime,
    endTime,
    purpose,
    othersPurpose,
    firstName,
    lastName,
    mobileNumber,
    gender,
    birthday,
    dentist?.id,
    createPatientMutation,
    createRequestMutation
  ]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLIonInputElement>) => {
      if (event.key === 'Enter') {
        submit();
      }
    },
    [submit]
  );

  useEffect(() => {
    if (selectedDate && isValid(selectedDate)) {
      setDate(format(startOfDay(selectedDate), 'yyyy-MM-dd'));
    } else {
      setDate(null);
    }
  }, [selectedDate]);

  useEffect(() => {
    return () => {
      setDate(null);
      setStartTime(null);
      setEndTime(null);
      setPurpose(null);
      setOthersPurpose(null);
      setFirstName(null);
      setLastName(null);
      setMobileNumber(null);
      setGender(null);
      setBirthday(null);
      setErrors({
        date: false,
        startTime: false,
        endTime: false,
        purpose: false,
        othersPurpose: false,
        firstName: false,
        lastName: false,
        mobileNumber: false
      });
    };
  }, []);

  const patients = useMemo(() => {
    return patientsByDentist
      .filter((patient) => !!patient)
      .filter((patient) => patient.id !== OFF_DUTY_PATIENT_ID)
      .reduce((uniquePatients: Patient[], patient: Patient) => {
        const normalizeName = (name: string | null | undefined) =>
          name?.toLowerCase()?.trim();

        if (
          !uniquePatients.some(
            (p) =>
              normalizeName(p.first_name) ===
                normalizeName(patient.first_name) &&
              normalizeName(p.last_name) === normalizeName(patient.last_name) &&
              p.mobile_number === patient.mobile_number
          )
        ) {
          uniquePatients.push(patient);
        }
        return uniquePatients;
      }, []);
  }, [patientsByDentist]);

  const conflictingRequest = useMemo(() => {
    if (
      !dentist?.id ||
      !date ||
      !startTime ||
      !endTime ||
      requests.length === 0
    ) {
      return null;
    }

    const slotStartTime = parseDate(`${date}T${startTime}`);
    const slotEndTime = parseDate(`${date}T${endTime}`);

    return (
      requests
        .filter(
          (request) =>
            isSameDay(parseDate(date), parseDate(request.date)) &&
            request.status === REQUEST_STATUS_OPTIONS.APPROVED
        )
        .find((request) => {
          const requestStartTime = parseDate(
            `${request.date}T${request.start_time}`
          );
          const requestEndTime = parseDate(
            `${request.date}T${request.end_time}`
          );

          return (
            ((isAfter(slotStartTime, requestStartTime) ||
              isEqual(slotStartTime, requestStartTime)) &&
              isBefore(slotStartTime, requestEndTime)) ||
            (isAfter(slotEndTime, requestStartTime) &&
              (isBefore(slotEndTime, requestEndTime) ||
                isEqual(slotEndTime, requestEndTime))) ||
            ((isAfter(requestStartTime, slotStartTime) ||
              isEqual(requestStartTime, slotStartTime)) &&
              isBefore(requestStartTime, slotEndTime)) ||
            (isAfter(requestEndTime, slotStartTime) &&
              (isBefore(requestEndTime, slotEndTime) ||
                isEqual(requestEndTime, slotEndTime)))
          );
        }) ?? null
    );
  }, [date, startTime, endTime, requests, dentist?.id]);

  // Purpose Selection

  const isPurposeSelected = (value: string) =>
    purpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const isPurposePreSelected = (value: string) =>
    lastSavedPurpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;

  const onSelectPurpose = (value: string, isSelected: boolean) => {
    if (purpose) {
      if (isSelected) {
        setPurpose(
          purpose
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== value)
            .join(PURPOSE_JOIN_SEPARATOR)
        );
      } else {
        setPurpose(purpose + PURPOSE_JOIN_SEPARATOR + value);
      }
    } else {
      setPurpose(value);
    }
  };

  const onClosePurposeSelectModalWithSave = () => {
    onClosePurposeSelectModal();
    setLastSavedPurpose(purpose);
  };

  const onPressPatient = async (patient: Patient) => {
    const formattedFirstName = formatName(patient.first_name);
    const formattedLastName = formatName(patient.last_name);

    const { value: confirmed } = await Dialog.confirm({
      title: 'Confirm',
      message: `Are you sure you want to book an appointment for ${formattedFirstName} ${formattedLastName}?`,
      okButtonTitle: 'Yes'
    });

    if (!confirmed) return;

    setFirstName(formattedFirstName);
    setLastName(formattedLastName);
    setErrors((prevErrors) => ({
      ...prevErrors,
      firstName: false,
      lastName: false
    }));
    if (patient.mobile_number) {
      setMobileNumber(patient.mobile_number);
      setErrors((prevErrors) => ({ ...prevErrors, mobileNumber: false }));
    }
    if (patient.gender) {
      setGender(patient.gender);
    }
    if (patient.birthday) {
      setBirthday(patient.birthday);
    }

    onClosePatientSearchModal();
  };

  const loading =
    createPatientMutation.isPending || createRequestMutation.isPending;

  return (
    <ManualBookingContext.Provider
      value={{
        date,
        setDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        timeOptions,
        purpose,
        setPurpose,
        othersPurpose,
        setOthersPurpose,
        purposeOptions: services,
        isPurposeSelected,
        isPurposePreSelected,
        onSelectPurpose,
        isPurposeSelectModalOpen,
        onOpenPurposeSelectModal,
        onClosePurposeSelectModal: onClosePurposeSelectModalWithSave,
        patients,
        isPatientSearchModalOpen,
        onOpenPatientSearchModal,
        onPressPatient,
        onClosePatientSearchModal,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        mobileNumber,
        setMobileNumber,
        gender,
        setGender,
        birthday,
        setBirthday,
        errors,
        setErrors,
        loading,
        onInput,
        submit,
        reset,
        onKeyDown,
        conflictingRequest,
        inputRef
      }}
    >
      {children}
    </ManualBookingContext.Provider>
  );
};

export const useManualBooking = () => {
  const context = useContext(ManualBookingContext);
  if (context === undefined) {
    throw new Error(
      'useManualBooking must be used within a ManualBookingProvider'
    );
  }
  return context;
};
